import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";

const base = (campusId: number) => `/admin/campus/${campusId}/plantillas`;

export type HeaderTipo = "none" | "text" | "image" | "video" | "document";
export type BotonTipo = "quick" | "url" | "phone" | "copy";
export type VisTipo = "yo" | "equipo" | "asesoras" | "territorio" | "todos";

export interface PlantillaBoton { tipo: BotonTipo; texto?: string; url?: string; telefono?: string; ejemplo?: string; }
export interface Visibilidad { tipo: VisTipo; equipo?: string | null; ids?: number[]; }
export interface RevisionIA {
	ok: boolean | null; aviso?: string; correcciones?: { antes: string; despues: string; por_que: string }[];
	texto?: string; titulo?: string | null; pie?: string | null; avisos?: string[]; revisada_at?: string;
	puntos?: number | null; criterios?: Record<string, number>; resumen?: string; mejorada?: string | null;
	/** La misma idea escrita como Aviso (Utility), que cuesta unas 6 veces menos; null si no aplica. */
	aviso_utility?: string | null;
}
/** Modelo de plantilla que sí vende, para copiar el estilo al crear una. */
export interface PlantillaEjemplo { titulo: string; categoria: "MARKETING" | "UTILITY"; dato: string; cuerpo: string; botones: PlantillaBoton[]; }
export interface Calidad { puntos: number | null; criterios: Record<string, number>; resumen: string; mejorada?: string | null; vigente?: boolean; at?: string; }
export interface Lto { activa: boolean; titulo: string; horas: number; }
export type AutoAccion =
	| { tipo: "texto"; texto: string }
	| { tipo: "archivo"; path?: string; clave?: string; nombre?: string; mime?: string; voz?: boolean }
	| { tipo: "status"; status_id: number }
	| { tipo: "avisar" };
export interface AutoRegla { disparo: string; acciones: AutoAccion[]; }
export interface PlantillaNumero { waba_id: string; numeros: string[]; estado: string; motivo: string | null; enviada_at: string | null; actualizado_at: string | null; }

export interface Plantilla {
	id: number; nombre: string; meta_name: string | null; dept: string; dept_nombre: string; territorio: string | null; territorio_nombre: string;
	categoria: "MARKETING" | "UTILITY"; idioma: string; header_tipo: HeaderTipo; lto: Lto | null; auto: AutoRegla[]; calidad: Calidad | null; header_texto: string | null; header_media_nombre: string | null;
	header_media_mime: string | null; tiene_media: boolean; cuerpo: string; pie: string | null; botones: PlantillaBoton[];
	ejemplos: Record<string, string>; variables: string[]; visibilidad: Visibilidad; revision: RevisionIA | null;
	estado: "borrador" | "por_aprobar" | "rechazada" | "aprobada" | "archivada"; estado_nombre: string; motivo: string | null; origen: string;
	creador: string | null; created_by: number; created_at: string; updated_at: string; aprobada_por: string | null; aprobada_at: string | null;
	numeros: PlantillaNumero[];
	puede: { editar: boolean; aprobar: boolean; archivar: boolean; version: boolean; visibilidad: boolean };
}

/** depts: solo se ofrece al armar plantillas de esos departamentos (los datos de la cuenta del alumno: Caja, Kits, Académico). */
export interface PlantillaVariable { clave: string; etiqueta: string; ejemplo: string; auto?: string; depts?: string[]; }
export interface PlantillasCatalogos {
	departamentos: { clave: string; nombre: string }[];
	territorios: { clave: string; nombre: string }[];
	alcance: Record<string, { territorios: string[]; supervisa: string[] }>;
	variables: PlantillaVariable[];
	ejemplos?: PlantillaEjemplo[];
	limites: { cuerpo: number; titulo: number; pie: number; botones: number; boton_texto: number;
		media: Record<string, { mimes: string[]; max: number; nombre: string }> };
	equipo: string | null;
	estados: Record<string, string>;
	statuses: { id: number; nombre: string }[];
	lto: { titulo: number; cuerpo: number };
	calidad_minima: number;
	/** La calificación con IA frena el envío solo si está prendida (por ahora apagada). */
	calificacion: boolean;
}
export interface Destino { waba_id: string; channel_ids: number[]; numeros: string[]; territorio: string; }
export interface DeMeta { meta_name: string; idioma: string; categoria: string; cuerpo: string; header: string | null; header_texto: string | null; params: number; cuentas: { waba_id: string; numeros: string[] }[]; }
/** Las que se pueden mandar por un número desde WappChat. */
export interface PlantillaEnvio {
	id: number; nombre: string; meta_name: string; idioma: string; dept: string; categoria: string; header_tipo: HeaderTipo; header_texto: string | null;
	cuerpo: string; pie: string | null; botones: PlantillaBoton[]; variables: { clave: string; etiqueta: string; valor: string }[]; tiene_media: boolean;
	lto?: Lto | null;
}

/** Plantillas de WhatsApp (Mensajería): sugerir, aprobar, crear en Meta y decidir quién las ve. */
export class PlantillasService {
	catalogos = async (c: number): Promise<AxiosResponse<PlantillasCatalogos>> => axiosInstance.get(`${base(c)}/_catalogos`);

	lista = async (c: number, f: { dept?: string; estado?: string; territorio?: string; q?: string }): Promise<AxiosResponse<{ data: Plantilla[]; pendientesMias: number }>> =>
		axiosInstance.get(base(c), { params: f });

	detalle = async (c: number, id: number): Promise<AxiosResponse<Plantilla>> => axiosInstance.get(`${base(c)}/${id}`);

	/** autoArchivos: clave → archivo de las respuestas automáticas (nota de voz, foto, video, PDF). */
	guardar = async (c: number, id: number | null, datos: any, archivo: File | null, accion: "borrador" | "sugerir" | "aprobar",
		autoArchivos: Record<string, File> = {}): Promise<AxiosResponse<Plantilla>> => {
		const form = new FormData();
		form.append("datos", JSON.stringify(datos));
		form.append("accion", accion);
		if (archivo) form.append("archivo", archivo, archivo.name);
		Object.entries(autoArchivos).forEach(([k, f]) => form.append(`auto_archivos[${k}]`, f, f.name));
		return axiosInstance.post(id ? `${base(c)}/${id}` : base(c), form, { headers: { "Content-Type": "multipart/form-data" } });
	};

	revisar = async (c: number, datos: any): Promise<AxiosResponse<{ reglas: string[]; sugerencias?: string[]; ia: RevisionIA }>> => {
		const form = new FormData();
		form.append("datos", JSON.stringify(datos));
		return axiosInstance.post(`${base(c)}/_revisar`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 } as any);
	};

	aprobar = async (c: number, id: number, cuentas?: string[]): Promise<AxiosResponse<Plantilla>> =>
		axiosInstance.post(`${base(c)}/${id}/_aprobar`, cuentas ? { cuentas } : {}, { timeout: 300000 } as any);
	rechazar = async (c: number, id: number, motivo: string): Promise<AxiosResponse<Plantilla>> => axiosInstance.post(`${base(c)}/${id}/_rechazar`, { motivo });
	archivar = async (c: number, id: number): Promise<AxiosResponse<Plantilla>> => axiosInstance.post(`${base(c)}/${id}/_archivar`, {});
	desarchivar = async (c: number, id: number): Promise<AxiosResponse<Plantilla>> => axiosInstance.post(`${base(c)}/${id}/_desarchivar`, {});
	visibilidad = async (c: number, id: number, visibilidad: Visibilidad): Promise<AxiosResponse<Plantilla>> => axiosInstance.put(`${base(c)}/${id}/_visibilidad`, { visibilidad });
	auto = async (c: number, id: number, auto: any[], archivos: Record<string, File>): Promise<AxiosResponse<Plantilla>> => {
		const form = new FormData();
		form.append("auto", JSON.stringify(auto));
		Object.entries(archivos).forEach(([k, f]) => form.append(`auto_archivos[${k}]`, f, f.name));
		return axiosInstance.post(`${base(c)}/${id}/_auto`, form, { headers: { "Content-Type": "multipart/form-data" } });
	};
	version = async (c: number, id: number): Promise<AxiosResponse<Plantilla>> => axiosInstance.post(`${base(c)}/${id}/_version`, {});
	archivo = async (c: number, id: number): Promise<AxiosResponse<Blob>> => axiosInstance.get(`${base(c)}/${id}/_archivo`, { responseType: "blob" });

	personas = async (c: number, dept: string, territorio: string | null): Promise<AxiosResponse<{ id: number; nombre: string }[]>> =>
		axiosInstance.get(`${base(c)}/_personas`, { params: { dept, territorio } });
	destinos = async (c: number, dept: string, territorio: string | null): Promise<AxiosResponse<Destino[]>> =>
		axiosInstance.get(`${base(c)}/_destinos`, { params: { dept, territorio } });
	deMeta = async (c: number, dept: string, territorio: string | null): Promise<AxiosResponse<DeMeta[]>> =>
		axiosInstance.get(`${base(c)}/_deMeta`, { params: { dept, territorio } });
	adoptar = async (c: number, dept: string, territorio: string | null, meta_name: string, idioma: string): Promise<AxiosResponse<Plantilla>> =>
		axiosInstance.post(`${base(c)}/_adoptar`, { dept, territorio, meta_name, idioma });
}
